import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';
import { impersonationAwareFetch } from '@command-center/auth-sdk/browser-impersonation';

export type SupabaseSession = Session;
export type SupabaseUser = User;

let client: SupabaseClient | null | undefined;

export function getSupabaseUrl(): string | undefined { return readEnv('VITE_SUPABASE_URL'); }
export function getSupabaseAnonKey(): string | undefined { return readEnv('VITE_SUPABASE_ANON_KEY'); }
export function getPublicAppUrl(): string {
  const configured = readEnv('VITE_PUBLIC_APP_URL');
  if (!configured) return window.location.origin;
  try {
    const url = new URL(configured);
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.origin
      : window.location.origin;
  } catch {
    return window.location.origin;
  }
}

export function getPasswordResetRedirectUrl(publicAppUrl = getPublicAppUrl()): string {
  return `${publicAppUrl.replace(/\/$/, '')}/auth/reset`;
}

export function getSupabaseBrowserClient(): SupabaseClient | null {
  if (client !== undefined) return client;
  const url = getSupabaseUrl();
  const key = getSupabaseAnonKey();
  client = url && key ? createClient(url, key, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  }) : null;
  return client;
}

export async function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const impersonated = await impersonationAwareFetch(input, init);
  if (impersonated) return impersonated;
  const supabase = getSupabaseBrowserClient();
  const session = supabase ? (await supabase.auth.getSession()).data.session : null;
  const response = await fetchWithSession(input, init, session);
  if (response.status !== 401 || !supabase) return response;
  const refreshed = await supabase.auth.refreshSession();
  if (!refreshed.data.session) {
    await supabase.auth.signOut({ scope: 'local' });
    return response;
  }
  const retry = await fetchWithSession(input, init, refreshed.data.session);
  if (retry.status === 401) await supabase.auth.signOut({ scope: 'local' });
  return retry;
}

export async function signInWithPassword(email: string, password: string) {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return { ok: false as const, error: 'Supabase env vars are not configured.' };
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data.session) return { ok: false as const, error: error?.message ?? 'Sign-in failed.' };
  return { ok: true as const, session: data.session };
}

export async function signOut(): Promise<void> { await getSupabaseBrowserClient()?.auth.signOut(); }

export async function clearLocalSession(): Promise<void> {
  await getSupabaseBrowserClient()?.auth.signOut({ scope: 'local' });
}

export async function requestPasswordReset(email: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return { ok: false, error: 'Supabase env vars are not configured.' };
  const redirectTo = getPasswordResetRedirectUrl();
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function updatePassword(password: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return { ok: false, error: 'Supabase env vars are not configured.' };
  const { error } = await supabase.auth.updateUser({ password });
  return error ? { ok: false, error: error.message } : { ok: true };
}

async function fetchWithSession(input: RequestInfo | URL, init: RequestInit, session: Session | null) {
  const headers = new Headers(init.headers);
  if (session?.access_token) headers.set('Authorization', `Bearer ${session.access_token}`);
  return fetch(input, { ...init, headers });
}

function readEnv(name: string): string | undefined {
  return (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.[name];
}
